"use client";

import { useState } from "react";
import Link from "next/link";
import LogoAnimatedGreeks from "@/components/logos/LogoAnimatedGreeks";


export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
    const [message, setMessage] = useState("");

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus("loading");
        setMessage("");

        try {
            const { authApi } = await import('@/lib/api-client');
            const result = await authApi.requestPasswordReset(email);

            if (result.success) {
                setStatus("success");
                setMessage("Password reset link sent! Please check your email.");
            } else {
                setStatus("error");
                setMessage(result.error || "Failed to send reset link. Please try again.");
            }
        } catch (err: any) {
            setStatus("error");
            setMessage(err.message || "Failed to send reset link. Please try again.");
        }
    };

    return (
        <div className="min-h-screen bg-brand-navy flex items-center justify-center px-6 py-12">
            <div className="max-w-md w-full">
                {/* Logo and Header */}
                <div className="text-center mb-8">
                    <Link href="/" className="inline-flex justify-center mb-6">
                        <LogoAnimatedGreeks className="w-20 h-20" interval={3000} />
                    </Link>
                    <h1 className="text-3xl font-montserrat font-bold text-white mb-2">
                        Reset Password
                    </h1>
                    <p className="text-gray-400">Enter your email to receive reset instructions</p>
                </div>

                {/* Form */}
                <div className="bg-white/5 border border-brand-blue/20 rounded-lg p-8">
                    {status === "success" ? (
                        <div className="text-center">
                            <div className="text-5xl mb-4">📧</div>
                            <h3 className="text-xl font-bold text-white mb-2">Check your inbox</h3>
                            <p className="text-gray-300 mb-6">{message}</p>
                            <Link
                                href="/login"
                                className="inline-block w-full px-4 py-3 bg-brand-blue hover:bg-brand-blue/90 text-white font-semibold rounded-lg transition-colors"
                            >
                                Return to Login
                            </Link>
                        </div>
                    ) : (
                        <>
                            {status === "error" && (
                                <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded mb-6 text-sm">
                                    {message}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue transition-colors"
                                        placeholder="you@example.com"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={status === "loading"}
                                    className="w-full bg-brand-orange hover:bg-brand-orange/90 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors"
                                >
                                    {status === "loading"
                                        ? "Sending..."
                                        : "Send Reset Link"}
                                </button>
                            </form>

                            <div className="mt-6 text-center text-sm">
                                <Link href="/login" className="text-gray-400 hover:text-white transition-colors">
                                    ← Back to Login
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
